import React, { useState, useCallback, useContext, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs, Tab, Alert, Box } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { PlainCard, Row, Filter, NotificationEmptyIcon, AppIcon, SystemIcon } from './components'
import Table from '@/components/Table'
import { notificationColumns } from './column'
import { NotificationDetail, NotificationType } from '@/types'
import { pageProps } from '@/components/Table'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  getNotifigations,
  readNotifications,
  deleteNotifications,
  getApplications,
  getUserOrganizations,
  readAllNotifications,
} from '@/api'
import Detail from './detail'
import Text from '@/components/Text'
import Button from '@/components/Button'
import { error, primary, defaultText, leastText, secondaryText } from '@/constant'
import { confirm } from '@/components/Modal'
import CounterBadge from '@/components/CounterBadge'
import { useDebounce } from '@/hooks'
import { KeywordSearchOutlined } from '@/components/Search'
import { NotificationContext } from '@/layout/NotificationIO'
import { Message } from '@cx/ui'

export const readStatusOptions = [
  { label: '无', value: '' },
  { label: '未读', value: 1 },
  { label: '已读', value: 2 },
]

//export type NotificationLevel = "high" | "medium" | "low";

export const severityLevels = [
  { label: '无', value: null },
  { label: '高', value: 'high' },
  { label: '中', value: 'medium' },
  { label: '低', value: 'low' },
]

const History: React.FC<unknown> = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { notificatioinData, refetchUnReadNumber } = useContext(NotificationContext)
  const messageId = searchParams?.get('messageId')
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [data, setData] = useState<NotificationDetail[]>([])
  const [rows, setRows] = useState<number>(10)

  const [dataType, setDataType] = useState<NotificationType>((searchParams?.get('type') as NotificationType) || 'app')

  const [readStatus, setReadStatus] = useState<number | string>('')
  const [orgFilter, setOrgFilter] = useState<string | number>('')
  const [appFilter, setAppFilter] = useState<string | number>('')

  const [selectedKeys, setSelectedKeys] = useState<Array<number | string>>([])

  const [keyword, setKeyword] = useState<string>('')
  const debounceInput = useDebounce(keyword, 500)
  const [unreadNumber, setUnreadNumber] = useState<{
    app_total_number: number
    plat_total_number: number
    total_number: number
  }>({
    app_total_number: notificatioinData?.app_total_number ?? 0,
    plat_total_number: notificatioinData?.plat_total_number ?? 0,
    total_number: notificatioinData?.total_number ?? 0,
  })

  const { data: market } = useQuery(['getApplications'], getApplications, {})

  const { data: orgs } = useQuery(['getUserOrganizationsNotification'], () =>
    getUserOrganizations({ privilege: 'manager' }),
  )

  const queryNotifications = useQuery(
    ['getNotifigations', page, rows, readStatus, dataType, debounceInput, appFilter, orgFilter],
    () =>
      getNotifigations({
        page,
        size: rows,
        read_status: readStatus as number,
        // @ts-ignore
        level: null,
        org_id: orgFilter,
        send_id: appFilter,
        origin: dataType,
        keywords: debounceInput,
      }),
    {
      onSuccess: (res) => {
        setTotal(res?.total)
        setData(res?.detail ?? [])
        setSelectedKeys([])
      },
      onError: () => {
        setTotal(0)
        setData([])
        setSelectedKeys([])
      },
    },
  )

  const deleteMutation = useMutation(deleteNotifications, {
    onSuccess: () => {
      Message.success('删除成功')
      setPage(1)
      setSelectedKeys([])
      if (page === 1) {
        queryNotifications.refetch()
      }
      refetchUnReadNumber()
    },
  })
  const readMutation = useMutation(readNotifications, {
    onSuccess: () => {
      Message.success('消息已读')
      queryNotifications.refetch()
      setSelectedKeys([])
      refetchUnReadNumber()
    },
  })
  const readAll = useMutation(readAllNotifications, {
    onSuccess: () => {
      Message.success('消息已读')
      refetchUnReadNumber()
    },
  })

  const handleDelete = useCallback(() => {
    if (selectedKeys?.length > 0) {
      confirm({
        onConfirm: () => {
          deleteMutation.mutate(selectedKeys as number[])
        },
        // @ts-ignore
        onClose: null,
        title: '提示',
        content: '确认删除已选消息',
        closeAfterClose: true,
      })
    }
  }, [deleteMutation, selectedKeys])

  const handleRead = useCallback(() => {
    if (selectedKeys?.length > 0) {
      confirm({
        onConfirm: () => {
          readMutation.mutate(selectedKeys as number[])
        },
        // @ts-ignore
        onClose: null,
        title: '提示',
        content: '确认标记已选消息为已读',
        closeAfterClose: true,
      })
    }
  }, [readMutation, selectedKeys])

  const handlePageChange = (value: pageProps) => {
    setPage(value.page)
    // @ts-ignore
    setRows(value.rowsPerPage)
  }

  const handleClose = useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  const handleClickRow = useCallback(
    (row: NotificationDetail) => {
      setSearchParams({ messageId: row?.id?.toString() })
    },
    [setSearchParams],
  )

  const onSelect = (checked: boolean, key: string | number) => {
    if (checked) {
      setSelectedKeys((selectedKeys) => [...selectedKeys, key])
    } else {
      const keyIndex = selectedKeys?.indexOf(key)
      const newKeys = [...selectedKeys]
      newKeys.splice(keyIndex, 1)
      setSelectedKeys([...newKeys])
    }
  }

  const onSelectAll = (checked: boolean, keys: Array<string | number>) => {
    if (checked) {
      setSelectedKeys([...keys])
    } else {
      setSelectedKeys([])
    }
  }

  const handleReadAll = useCallback(() => {
    //mark all messages as read
    if (total) {
      readAll?.mutate({ origin: dataType })
    }
  }, [total, dataType, readAll])

  const handleChangeDataType = useCallback((event: React.SyntheticEvent, newValue: NotificationType) => {
    setPage(1)
    setDataType(newValue)
    // @ts-ignore
    setAppFilter(null)
    // @ts-ignore
    setOrgFilter(null)
  }, [])

  const handleChangeReadState = useCallback((status: string | number) => {
    setPage(1)
    setReadStatus(status)
  }, [])

  const handleChangeOrgFilter = useCallback((orgId: string | number) => {
    setPage(1)
    setOrgFilter(orgId)
  }, [])

  const handleChangeAppFilter = useCallback((appId: string | number) => {
    setPage(1)
    setAppFilter(appId)
  }, [])

  const handleChangeSearchKeyWords = useCallback((keywords: string) => {
    setKeyword(keywords)
  }, [])

  const onReadDetail = useCallback(() => {
    queryNotifications?.refetch()
    refetchUnReadNumber()
  }, [queryNotifications, refetchUnReadNumber])

  useEffect(() => {
    setUnreadNumber({
      app_total_number: notificatioinData?.app_total_number,
      plat_total_number: notificatioinData?.plat_total_number,
      total_number: notificatioinData?.total_number,
    })
    //eslint-disable-next-line
  }, [notificatioinData])

  useEffect(() => {
    if (notificatioinData?.total_number !== unreadNumber?.total_number) {
      queryNotifications.refetch()
    }
    //eslint-disable-next-line
  }, [notificatioinData, unreadNumber])

  return (
    <PlainCard sx={{ gap: '8px', position: 'relative' }}>
      <Row sx={{ gap: '16px' }}>
        <Tabs
          value={dataType}
          onChange={handleChangeDataType}
          sx={{
            minHeight: 'auto',
            '& .MuiTabs-flexContainer': {
              height: '100%',
              alignItems: 'center',
              gap: '32px',
            },
          }}
        >
          <Tab
            sx={{ padding: '4px 0px', minHeight: 'auto' }}
            disableRipple={true}
            value={'app'}
            label={
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <AppIcon active={dataType === 'app'} />
                产品消息
                <CounterBadge number={unreadNumber?.app_total_number} max={99} />
              </Box>
            }
          />
          <Tab
            sx={{ padding: '4px 0px', minHeight: 'auto' }}
            disableRipple={true}
            value={'platform'}
            label={
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <SystemIcon active={dataType === 'platform'} />
                平台消息
                <CounterBadge number={unreadNumber?.plat_total_number} max={99} />
              </Box>
            }
          />
        </Tabs>
        {dataType === 'app' && (
          <>
            <Filter
              sx={{ marginLeft: 'auto' }}
              label='应用'
              // @ts-ignore
              options={
                market?.data && [
                  { label: '无', value: '' },
                  ...market?.data?.map((app) => ({
                    label: app?.name,
                    value: app?.id,
                  })),
                ]
              }
              value={appFilter?.toString()}
              onChange={handleChangeAppFilter}
            />
            <Filter
              label='空间'
              // @ts-ignore
              options={
                orgs?.data && [
                  { label: '无', value: '' },
                  ...orgs?.data?.map((org) => ({
                    label: org?.name,
                    value: org?.id,
                  })),
                ]
              }
              value={orgFilter?.toString()}
              onChange={handleChangeOrgFilter}
            />
          </>
        )}
        <Filter
          // @ts-ignore
          sx={dataType === 'platform' && { marginLeft: 'auto' }}
          label='状态'
          options={readStatusOptions}
          value={readStatus?.toString()}
          onChange={handleChangeReadState}
        />
        <KeywordSearchOutlined
          placeholder='搜索消息'
          clear={true}
          value={keyword}
          // @ts-ignore
          onChange={handleChangeSearchKeyWords}
          // @ts-ignore
          onSearch={null}
        />
      </Row>
      {dataType === 'app' && (
        <Alert
          severity='info'
          sx={{ alignItems: 'center' }}
          icon={
            <InfoIcon
              sx={{
                color: '#2f7ce9',
                fontSize: '14px',
              }}
            />
          }
        >
          <Text size='xs' sx={{ color: secondaryText, display: 'flex', alignItems: 'center' }}>
            提醒：保留最近14天消息
          </Text>
        </Alert>
      )}
      <Table<NotificationDetail>
        columns={notificationColumns(dataType)}
        loading={queryNotifications?.isLoading}
        rowKey={'id'}
        data={data}
        onRowClick={handleClickRow}
        selectionProps={{
          selectedKeys,
          onSelect,
          onSelectAll,
          selectActions: [
            <Text key='select-action-title' size='xs' weight='regular'>
              （已选{selectedKeys?.length}项）
            </Text>,
            <Button
              key='select-action-read'
              type='text'
              sx={{ padding: '0 8px', minWidth: 'auto', fontSize: '14px' }}
              onClick={handleRead}
            >
              标记为已读
            </Button>,
            <Button
              key='select-action-delete'
              type='text'
              sx={{
                color: error,
                padding: '0 8px',
                minWidth: 'auto',
                fontSize: '14px',
              }}
              onClick={handleDelete}
            >
              删除
            </Button>,
          ],
        }}
        pagination={{
          page,
          total,
          onChange: handlePageChange,
          rowsPerPage: rows,
        }}
        empty={
          <>
            <NotificationEmptyIcon />
            <Text size='xs' sx={{ color: leastText }}>
              暂无消息
            </Text>
          </>
        }
      />
      <Button
        sx={{
          position: 'absolute',
          bottom: '28px',
          left: '260px',
        }}
        disabled={!total}
        onClick={handleReadAll}
      >
        全部已读
      </Button>
      <Detail
        dataType={dataType}
        // @ts-ignore
        id={messageId}
        open={!!messageId}
        onClose={handleClose}
        refecth={onReadDetail}
      />
    </PlainCard>
  )
}

export default History
